import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { format } from "date-fns";
import Lenis from "lenis";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import {
  Shield, Database, Calendar as CalendarIcon, Lock, Clock,
  CheckCircle2, ChevronRight, Info, ArrowRight, ArrowUpRight, X, Fingerprint,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { DATASETS, SCHEMA_FIELDS } from "@/lib/datasets";
import PricingSection from "@/components/PricingSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MARQUEE_ITEMS = [
  "10 AGENT-SKILL DATASETS", "~1,000 LABELLED RECORDS", "RISK-TIERED DECISIONS",
  "HUMAN-REVIEW FLAGGED", "PRIVACY-SAFE · DERIVED PATTERNS", "MONTHLY UPDATE CYCLE",
  "PROMPT-READY CSV", "CONSENT-AWARE BY DESIGN",
];

const HERO_LINES = ["BUILD THE AGENT", "THIS INDUSTRY HAS", "BEEN WAITING FOR."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function LandingPage() {
  const [activeDataset, setActiveDataset] = useState(null);

  // Booking state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingForm, setBookingForm] = useState({ name: "", email: "", company: "", use_case: "" });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);

  // Custom Agent Skill Development — quote request (reuses waitlist backend)
  const [quoteForm, setQuoteForm] = useState({ name: "", email: "", company: "", use_case: "", budget: "A$4,000 – A$6,000" });
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);

  const heroRef = useRef(null);

  // Lenis smooth scroll
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    // anchor links routed through lenis
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const el = document.querySelector(a.getAttribute("href"));
      if (el) {
        e.preventDefault();
        lenis.scrollTo(el, { offset: -80 });
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      document.removeEventListener("click", onClick);
    };
  }, []);

  useEffect(() => {
    if (selectedDate) fetchSlots(selectedDate);
  }, [selectedDate]);

  const fetchSlots = async (date) => {
    setSlotsLoading(true);
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const { data } = await axios.get(`${API}/bookings/available-slots?date=${dateStr}`);
      setAvailableSlots(data.slots || []);
      setSelectedSlot(null);
    } catch {
      /* ignore */
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;
    setBookingLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      await axios.post(`${API}/bookings`, { ...bookingForm, date: dateStr, time_slot: selectedSlot });
      setBookingSuccess(`Walkthrough booked for ${dateStr} at ${selectedSlot}.`);
      toast.success("Consultation booked!");
      setBookingForm({ name: "", email: "", company: "", use_case: "" });
      setSelectedSlot(null);
      setShowBookingForm(false);
      fetchSlots(selectedDate);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not book. Slot may be taken.");
    } finally {
      setBookingLoading(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setQuoteLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        name: quoteForm.name,
        email: quoteForm.email,
        company: quoteForm.company,
        use_case: quoteForm.use_case,
        interest_tier: "custom_skill_quote",
        notes: `Custom Agent Skill Development quote · Budget: ${quoteForm.budget}`,
      });
      setQuoteSent(true);
      toast.success("Quote request sent");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send request. This email may already be on file.");
    } finally {
      setQuoteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#FF3B30] selection:text-white relative font-sans overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#050505]/85 backdrop-blur-xl border-b border-white/10 px-6 py-4 md:px-12 lg:px-24 flex justify-between items-center">
        <a href="#top" className="flex items-center gap-3 group">
          <Logo size={26} className="text-white transition-transform duration-300 group-hover:rotate-3" />
          <span className="text-lg font-black tracking-widest font-mono">PREMI<span className="text-[#FF3B30]">-SKILLS</span></span>
          <span className="hidden md:inline-block text-[10px] font-mono text-[#30D158] ml-2 tracking-[0.2em] border-l border-white/10 pl-3">SKILLS_ONLINE</span>
        </a>
        <nav className="flex items-center gap-6">
          <a href="#catalogue" className="hidden sm:inline text-xs uppercase tracking-wider font-mono hover:text-[#FF3B30] transition-colors">Catalogue</a>
          <a href="#pricing" className="hidden sm:inline text-xs uppercase tracking-wider font-mono hover:text-[#FF3B30] transition-colors">Pricing</a>
          <a href="#booking" className="hidden md:inline text-xs uppercase tracking-wider font-mono hover:text-[#FF3B30] transition-colors">Walkthrough</a>
          <Link to="/admin" className="border border-white/20 bg-white/5 hover:bg-white hover:text-black px-4 py-2 text-xs uppercase tracking-wider font-mono transition-colors" data-testid="admin-panel-link">Console</Link>
        </nav>
      </header>

      {/* Hero */}
      <section id="top" ref={heroRef} className="relative px-6 md:px-12 lg:px-24 pt-20 pb-24 md:pt-28 md:pb-32 grid-texture">
        <div className="absolute top-0 right-0 w-[45%] h-[420px] bg-gradient-to-bl from-[#FF3B30]/10 to-transparent pointer-events-none" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end relative z-10">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3 py-1 bg-red-950/30 border border-[#FF3B30]/30 text-[#FF3B30] text-xs font-mono mb-8 uppercase tracking-widest"
            >
              <Shield className="w-3.5 h-3.5" /> Privacy-Safe Derived Datasets · Licensed Access
            </motion.div>

            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.92] uppercase mb-8">
              {HERO_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span className="block" custom={i} variants={lineVariants} initial="hidden" animate="visible">
                    {i === 2 ? (
                      <>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/40">BEEN WAITING</span>{" "}
                        <span className="text-[#FF3B30]">FOR.</span>
                      </>
                    ) : line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
              className="text-lg text-[#C7C7CC] leading-relaxed max-w-2xl mb-10"
            >
              Adult-industry workers have businesses to run — they shouldn't need to become agent developers to get
              technology that understands their work. Our privacy-safe, industry-specific datasets and agent-skill
              instructions give you a foundation to build from, with
              <span className="text-white font-mono"> monthly updates</span> throughout your subscription.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85 }}
              className="flex flex-wrap gap-4 font-mono"
            >
              <a href="#pricing" className="bg-white text-black hover:bg-[#FF3B30] hover:text-white px-8 py-4 font-bold uppercase tracking-wider text-xs transition-colors flex items-center gap-2 active:scale-[0.98]" data-testid="hero-pricing-btn">
                License Access <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#catalogue" className="border border-white/20 hover:border-white px-8 py-4 uppercase tracking-wider text-xs transition-colors flex items-center gap-2" data-testid="hero-catalogue-btn">
                Explore Datasets <Database className="w-4 h-4" />
              </a>
            </motion.div>
          </div>

          {/* Evidence panel */}
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5 }}
            className="lg:col-span-4 border border-white/10 bg-[#0c0d0e] p-6"
          >
            <div className="flex items-center justify-between text-[10px] font-mono text-[#8E8E93] uppercase tracking-widest border-b border-white/10 pb-3 mb-5">
              <span>Catalogue Facts</span>
              <span className="flex items-center gap-1.5 text-[#30D158]"><span className="w-1.5 h-1.5 bg-[#30D158] rounded-full animate-pulse" /> PRIVACY-SAFE</span>
            </div>
            {[
              ["10", "Agent-skill datasets"],
              ["~1,000", "Labelled decision records"],
              ["3-TIER", "Risk model · LOW / MED / HIGH"],
              ["MONTHLY", "Update & release cycle"],
            ].map(([v, l], i) => (
              <div key={i} className="py-3 border-b border-white/5 last:border-0">
                <div className={`font-mono text-2xl font-black ${i % 2 === 0 ? "text-[#FF3B30]" : "text-white"}`}>{v}</div>
                <div className="text-[11px] uppercase tracking-widest font-mono text-[#8E8E93] mt-0.5">{l}</div>
              </div>
            ))}
            <p className="text-[10px] font-mono text-[#48484A] leading-relaxed mt-4 pt-3 border-t border-white/5">
              Privacy-safe derived patterns — not raw messages or real PII. High-risk records are flagged for human review; model performance is not claimed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-white/10 bg-[#0c0d0e] py-4 marquee-mask overflow-hidden">
        <div className="marquee-track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="mx-8 text-xs font-mono uppercase tracking-[0.2em] text-[#8E8E93] inline-flex items-center gap-8">
              {item} <span className="text-[#FF3B30]">/</span>
            </span>
          ))}
        </div>
      </div>

      {/* Why ground truth */}
      <section className="px-6 md:px-12 lg:px-24 py-24 border-b border-white/10 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] font-mono text-[#FF3B30] mb-3">// The Foundation</div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter leading-none mb-6">SCRAPED LISTINGS AREN'T A FOUNDATION.</h2>
            <p className="text-[#8E8E93] leading-relaxed">
              The value isn't in listed rates or public profiles — it's in the difficult conversations: knowing what
              to ask, when to pause, and when to hand control back to a human. These datasets are privacy-safe,
              derived interpretations built to seed exactly that kind of agent, with clear limitations stated up front.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              [Fingerprint, "No source PII", "No original messages, names, numbers, locations, dates or amounts. Only generalized, derived interpretations remain."],
              [Database, "Intent-structured", "Every record is a chat example labelled with intent, sentiment and urgency — ready for prompt design and routing."],
              [Shield, "Consent-aware by design", "Built to respect boundaries and escalate high-risk moments to qualified human review, not automate them."],
              [CheckCircle2, "Prompt-ready delivery", "UTF-8 CSV with a clear decision schema and per-file manifest. Ready for prompt design, routing and controlled fine-tuning — performance is not claimed."],
            ].map(([Icon, t, d], i) => (
              <motion.div
                key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-white/10 bg-[#111315] p-6 hover:border-white/25 transition-colors"
              >
                <Icon className="w-6 h-6 text-[#FF3B30] mb-4" />
                <h3 className="text-base font-bold mb-2">{t}</h3>
                <p className="text-xs text-[#8E8E93] leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dataset Catalogue Bento */}
      <section id="catalogue" className="px-6 md:px-12 lg:px-24 py-24 border-b border-white/10 relative z-10">
        <div className="mb-14 max-w-2xl">
          <div className="text-xs uppercase tracking-[0.25em] font-mono text-[#FF3B30] mb-3">// The Catalogue</div>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter leading-none mb-4">TEN SKILLS. ONE CATALOGUE.</h2>
          <p className="text-[#8E8E93] leading-relaxed">
            Each dataset is a self-contained agent skill with its own monthly update cycle. Mix and match on any
            plan, or take the whole catalogue. Click any card to inspect its schema and scenarios.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-4">
          {DATASETS.map((d, i) => (
            <motion.button
              key={d.id} onClick={() => setActiveDataset(d)}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.05 }}
              className={`group text-left border border-white/10 bg-[#111315] hover:border-[#FF3B30]/50 transition-colors p-7 flex flex-col justify-between min-h-[220px] ${d.span}`}
              data-testid={`dataset-card-${d.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-xs font-mono text-[#FF3B30] tracking-widest uppercase">{d.kicker}</span>
                  <h3 className={`font-bold tracking-tight text-white mt-2 ${d.span.includes("col-span-8") ? "text-2xl sm:text-3xl" : "text-xl"}`}>{d.name}</h3>
                </div>
                <span className="font-mono text-xs text-[#48484A] group-hover:text-[#FF3B30] transition-colors">{d.index}</span>
              </div>
              <p className="text-[#8E8E93] text-sm leading-relaxed my-4">{d.tagline}</p>
              <div className="flex items-center justify-between border-t border-white/10 pt-4">
                <span className="text-[11px] font-mono text-[#8E8E93] uppercase tracking-wider">{d.records} records · {d.riskRange}</span>
                <span className="text-xs font-mono text-white flex items-center gap-1 group-hover:text-[#FF3B30] transition-colors">Inspect <ArrowUpRight className="w-3.5 h-3.5" /></span>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Workforce narrative */}
      <section className="px-6 md:px-12 lg:px-24 py-24 bg-[#0c0d0e] border-b border-white/10 relative z-10">
        <div className="max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-mono text-[#FF3B30] mb-3">// The Opportunity</div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter leading-none mb-6">BUILD SOMETHING WORTH PAYING FOR.</h2>
            <p className="text-[#8E8E93] leading-relaxed mb-4">
              For builders, this is an opening: develop an agent that handles everyday enquiries while respecting the
              worker's rates, boundaries and booking rules. The value is in how well it handles the difficult
              conversations — knowing what to ask, when to pause, and when to hand control back.
            </p>
            <p className="text-white text-sm font-medium leading-relaxed">
              Build something customers find useful enough to keep paying for, and you have the foundation for
              recurring revenue.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-mono text-[#30D158] mb-3">// Always Current</div>
            <h3 className="text-xl font-bold tracking-tight mb-4 border-b border-white/10 pb-3">Monthly updates, not a static dump</h3>
            <p className="text-[#8E8E93] leading-relaxed mb-5">
              Your subscription isn't a one-time file. Throughout your subscription we release new validated
              interpretations and skill instructions as they become available — no minimum update volume is
              guaranteed, but your foundation keeps growing while competitors stay frozen.
            </p>
            <div className="bg-[#111315] p-5 border-l-2 border-[#FF3B30] font-mono text-sm text-white leading-relaxed">
              "Workers shouldn't need to become agent developers to get technology that understands their work."
            </div>
          </div>
        </div>

        {/* Custom Agent Skill Development — optional, quote-based (no automatic charge) */}
        <div id="custom-development" className="border border-[#FF3B30]/40 bg-gradient-to-br from-[#FF3B30]/10 to-transparent p-8 md:p-10" data-testid="custom-development-block">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-6">
              <div className="text-xs uppercase tracking-[0.25em] font-mono text-[#FF3B30] mb-3">// Optional · Custom Agent Skill Development</div>
              <h3 className="text-2xl sm:text-3xl font-black tracking-tighter mb-4">NEED A BESPOKE SKILL BUILT?</h3>
              <p className="text-[#C7C7CC] leading-relaxed mb-6">
                Beyond the subscription catalogue, we take on one-off custom skill-development projects. Tell us the
                workflow you need your agent to learn and we'll scope, build and deliver a clean, structured skill set
                with instructions and integration notes.
              </p>
              <div className="font-mono text-3xl font-black tracking-tighter text-white mb-1">A$4,000 – A$8,000</div>
              <div className="font-mono text-xs text-[#8E8E93] mb-6">per project · quote-based · no automatic charge</div>
              <ul className="space-y-2.5">
                {["Scoped to your specific workflow", "Delivered as CSV + skill instructions + integration notes", "Fixed quote agreed before any work begins"].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-white leading-snug">
                    <CheckCircle2 className="w-4 h-4 text-[#30D158] flex-shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-6 border-l border-white/10 lg:pl-8">
              {quoteSent ? (
                <div className="py-10 text-center" data-testid="quote-success">
                  <CheckCircle2 className="w-12 h-12 text-[#30D158] mx-auto mb-4" />
                  <h4 className="text-xl font-black uppercase mb-2">Request Received</h4>
                  <p className="text-[#8E8E93] text-sm">We'll review your project and reply with a fixed quote. No charge is made until you approve it.</p>
                </div>
              ) : (
                <form onSubmit={handleQuoteSubmit} className="space-y-4" data-testid="quote-form">
                  <div className="text-xs font-mono uppercase tracking-widest text-[#FF3B30] mb-1">Request a project quote</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input required placeholder="Full name" value={quoteForm.name} onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })} className="bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none transition-colors" data-testid="quote-name-input" />
                    <input required placeholder="Company" value={quoteForm.company} onChange={(e) => setQuoteForm({ ...quoteForm, company: e.target.value })} className="bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none transition-colors" data-testid="quote-company-input" />
                  </div>
                  <input required type="email" placeholder="Work email" value={quoteForm.email} onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })} className="w-full bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none transition-colors" data-testid="quote-email-input" />
                  <select value={quoteForm.budget} onChange={(e) => setQuoteForm({ ...quoteForm, budget: e.target.value })} className="w-full bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none transition-colors font-mono" data-testid="quote-budget-select">
                    <option>A$4,000 – A$6,000</option>
                    <option>A$6,000 – A$8,000</option>
                    <option>Not sure yet</option>
                  </select>
                  <textarea required rows="3" placeholder="Describe the skill / workflow you need (min 5 characters)" value={quoteForm.use_case} onChange={(e) => setQuoteForm({ ...quoteForm, use_case: e.target.value })} className="w-full bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none transition-colors resize-none" data-testid="quote-usecase-textarea" />
                  <button type="submit" disabled={quoteLoading} className="w-full bg-[#FF3B30] text-white hover:bg-[#ff5449] font-mono font-bold py-3.5 uppercase tracking-wider text-xs transition-colors flex items-center justify-center gap-2 disabled:opacity-60" data-testid="quote-submit-btn">
                    {quoteLoading ? "Sending…" : <>Request a quote <ArrowRight className="w-4 h-4" /></>}
                  </button>
                  <p className="text-[10px] font-mono text-[#48484A] text-center">No automatic charge. We reply with a fixed quote for your approval.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />

      {/* Booking / walkthrough */}
      <section id="booking" className="px-6 md:px-12 lg:px-24 py-24 border-t border-white/10 relative z-10">
        <div className="mb-12 max-w-2xl">
          <div className="text-xs uppercase tracking-[0.25em] font-mono text-[#FF3B30] mb-3">// Verify First</div>
          <h2 className="text-4xl font-black tracking-tighter leading-none mb-4">BOOK A DATA WALKTHROUGH</h2>
          <p className="text-[#8E8E93] leading-relaxed">
            Want to inspect schemas, sample records and our privacy boundary before you license? Book a 15-minute
            secure walkthrough of the catalogue with our technical team.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          <div className="lg:col-span-5 bg-[#111315] border border-white/10 p-6 flex flex-col">
            <span className="block text-xs font-mono text-[#FF3B30] uppercase tracking-widest mb-4">[Step 1 · Choose a date]</span>
            <div className="flex justify-center bg-black/40 p-4 border border-white/5">
              <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} className="text-white" disabled={{ before: new Date() }} data-testid="booking-calendar" />
            </div>
            <div className="pt-5 mt-5 border-t border-white/5 text-xs text-[#8E8E93] font-mono flex items-center gap-2">
              <Info className="w-4 h-4 text-[#FF3B30] flex-shrink-0" /> Double-bookings are rejected automatically.
            </div>
          </div>

          <div className="lg:col-span-7 bg-[#111315] border border-white/10 p-8 flex flex-col">
            <span className="block text-xs font-mono text-[#FF3B30] uppercase tracking-widest mb-4">[Step 2 · Available slots]</span>
            <div className="text-sm mb-6 flex justify-between items-center bg-black/40 p-4 border border-white/5 font-mono">
              <span className="text-[#8E8E93]">Selected:</span>
              <span className="font-bold">{selectedDate ? format(selectedDate, "PPP") : "None"}</span>
            </div>

            {bookingSuccess ? (
              <div className="py-12 text-center" data-testid="booking-success-container">
                <CheckCircle2 className="w-14 h-14 text-[#30D158] mx-auto mb-4" />
                <h3 className="text-2xl font-black mb-2 uppercase">Booking Locked</h3>
                <p className="text-[#8E8E93] text-sm">{bookingSuccess}</p>
                <button onClick={() => setBookingSuccess(null)} className="mt-6 border border-white/20 hover:border-white text-xs font-mono py-2 px-4 transition-colors">Schedule Another</button>
              </div>
            ) : showBookingForm ? (
              <form onSubmit={handleBookingSubmit} className="space-y-5">
                <div className="flex justify-between items-center text-xs font-mono border-b border-white/10 pb-4">
                  <span className="text-[#FF3B30]">SLOT: {selectedSlot}</span>
                  <button type="button" onClick={() => setShowBookingForm(false)} className="text-[#8E8E93] hover:text-white">[cancel]</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input required placeholder="Your name" value={bookingForm.name} onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })} className="bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none" data-testid="booking-name-input" />
                  <input required type="email" placeholder="Email" value={bookingForm.email} onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })} className="bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none" data-testid="booking-email-input" />
                </div>
                <input required placeholder="Company" value={bookingForm.company} onChange={(e) => setBookingForm({ ...bookingForm, company: e.target.value })} className="w-full bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none" data-testid="booking-company-input" />
                <textarea required rows="3" placeholder="What would you like to verify in the demo?" value={bookingForm.use_case} onChange={(e) => setBookingForm({ ...bookingForm, use_case: e.target.value })} className="w-full bg-black border border-white/10 focus:border-[#FF3B30] py-3 px-4 text-sm outline-none resize-none" data-testid="booking-use-case-textarea" />
                <button type="submit" disabled={bookingLoading} className="w-full bg-white text-black hover:bg-[#FF3B30] hover:text-white font-mono font-bold py-3 uppercase tracking-wider text-xs transition-colors" data-testid="booking-submit-button">{bookingLoading ? "Confirming…" : "Secure Walkthrough"}</button>
              </form>
            ) : (
              <div>
                {slotsLoading ? (
                  <div className="flex flex-col items-center justify-center py-16 text-[#8E8E93] font-mono text-xs gap-3">
                    <div className="w-6 h-6 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" /> Checking calendar…
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="py-12 border border-white/5 bg-black/40 text-center px-4 text-xs text-[#8E8E93] font-mono">
                    [No slots available] — select another day.
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {availableSlots.map((slot) => (
                      <button key={slot} onClick={() => setSelectedSlot(slot)} className={`py-3 text-xs font-mono border transition-all ${selectedSlot === slot ? "bg-white text-black border-white" : "bg-black text-[#8E8E93] border-white/10 hover:border-[#FF3B30] hover:text-white"}`} data-testid={`time-slot-btn-${slot}`}>{slot}</button>
                    ))}
                  </div>
                )}
                {selectedSlot && (
                  <button onClick={() => setShowBookingForm(true)} className="w-full bg-[#FF3B30] text-white hover:bg-[#ff5449] font-mono font-bold py-3 uppercase tracking-wider text-xs transition-colors mt-8 flex items-center justify-center gap-2" data-testid="booking-continue-btn">
                    Confirm {selectedSlot} <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 px-6 py-14 md:px-12 lg:px-24 text-xs font-mono text-[#8E8E93] relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          <div>
            <div className="flex items-center gap-2 mb-3"><Logo size={22} className="text-white" /><span className="text-white font-black tracking-widest">PREMI-SKILLS</span></div>
            <p className="max-w-md leading-relaxed">
              Premi-Skills licenses privacy-safe, derived dataset interpretations — not original messages or raw data.
              Products are a foundation for development, not legal advice or proof of production safety. Buyers must
              apply human review and restrict all use to lawful interactions involving verified adults.
            </p>
          </div>
          <div className="md:text-right space-y-2">
            <div>CONSOLE: <Link to="/admin" className="text-[#FF3B30] hover:underline" data-testid="footer-console-link">SYSTEM ADMIN</Link></div>
            <div>SECURE PAYMENTS VIA STRIPE</div>
            <div>&copy; 2026 PREMI-SKILLS. ALL RIGHTS RESERVED.</div>
          </div>
        </div>
      </footer>

      {/* Dataset inspector modal */}
      <AnimatePresence>
        {activeDataset && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setActiveDataset(null)} data-testid="dataset-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.2 }} onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0c0d0e] border border-white/15 p-8"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="text-xs font-mono text-[#FF3B30] tracking-widest uppercase">{activeDataset.kicker} · {activeDataset.index}</span>
                  <h3 className="text-2xl font-black tracking-tight mt-2">{activeDataset.name}</h3>
                </div>
                <button onClick={() => setActiveDataset(null)} className="text-[#8E8E93] hover:text-white" data-testid="dataset-modal-close"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-[#C7C7CC] leading-relaxed mb-6">{activeDataset.description}</p>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div className="border border-white/10 bg-black/40 p-4">
                  <div className="text-2xl font-black font-mono text-[#FF3B30]">{activeDataset.records}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[#8E8E93] mt-1">Labelled decision records</div>
                </div>
                <div className="border border-white/10 bg-black/40 p-4">
                  <div className="text-2xl font-black font-mono text-[#30D158]">{activeDataset.riskRange}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[#8E8E93] mt-1">Risk tiers covered</div>
                </div>
              </div>
              <p className="text-[10px] font-mono text-[#48484A] leading-relaxed mb-6">
                Privacy-safe derived patterns — not raw messages or real PII. High-risk records carry a
                requires_human_review flag; model performance is not claimed.
              </p>
              <div className="mb-6">
                <div className="text-xs font-mono uppercase tracking-wider text-[#8E8E93] mb-3">Schema fields</div>
                <div className="flex flex-wrap gap-2">
                  {SCHEMA_FIELDS.map((s) => (
                    <span key={s} className="text-xs font-mono bg-[#111315] border border-white/10 px-2.5 py-1 text-[#C7C7CC]">{s}</span>
                  ))}
                </div>
              </div>
              <div className="mb-8">
                <div className="text-xs font-mono uppercase tracking-wider text-[#8E8E93] mb-3">Scenario coverage</div>
                <div className="flex flex-wrap gap-2">
                  {activeDataset.scenarios.map((s) => (
                    <span key={s} className="text-xs font-mono bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] px-2.5 py-1">{s}</span>
                  ))}
                </div>
              </div>
              <a href="#pricing" onClick={() => setActiveDataset(null)} className="w-full bg-white text-black hover:bg-[#FF3B30] hover:text-white font-mono font-bold py-3 uppercase tracking-wider text-xs transition-colors flex items-center justify-center gap-2" data-testid="dataset-modal-license-btn">
                License this dataset <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
